import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useHelpdeskLanguage } from '../../context/HelpdeskLanguageContext';
import './Helpdesk.css';

function HelpdeskTickets() {
    const { t, language, toggleLanguage, isRTL } = useHelpdeskLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [agents, setAgents] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    
const savedFilters = JSON.parse(sessionStorage.getItem('helpdesk_filters') || 'null');

const [filters, setFilters] = useState(savedFilters || {
    search: searchParams.get('search') || '',
    status: searchParams.get('status')?.split(',') || ['open', 'assigned', 'in_progress', 'waiting_for_customer'],
    priority: searchParams.get('priority') || '',
    category_id: searchParams.get('category_id') || '',
    assigned_to: searchParams.get('assigned_to') || (searchParams.get('filter') === 'my' ? 'me' : (searchParams.get('filter') === 'unassigned' ? 'unassigned' : '')),

    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    hide_closed: searchParams.get('hide_closed') === 'true'
});

const savedSort = JSON.parse(sessionStorage.getItem('helpdesk_sort') || 'null');

const [sortConfig, setSortConfig] = useState(savedSort || {
    field: 'created_at',
    direction: 'desc'
});


    useEffect(() => {
        loadCategories();
        loadAgents();
    }, []);
    
    

    useEffect(() => {
        loadTickets();
    }, [filters, pagination.page, sortConfig]);

useEffect(() => {
    sessionStorage.setItem('helpdesk_filters', JSON.stringify(filters));
}, [filters]);

useEffect(() => {
    sessionStorage.setItem('helpdesk_sort', JSON.stringify(sortConfig));
}, [sortConfig]);


    const loadCategories = async () => {
        try {
            const response = await api.get('/helpdesk-agent/categories');
            if (response.data.success) {
                setCategories(response.data.categories || []);
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadAgents = async () => {
        try {
            const response = await api.get('/helpdesk-agent/agents');
            if (response.data.success) {
                setAgents(response.data.agents || []);
            }
        } catch (error) {
            console.error('Failed to load agents:', error);
        }
    };

    const loadTickets = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            if (filters.search) params.append('search', filters.search);
            if (filters.status.length > 0) params.append('status', filters.status.join(','));
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.category_id) params.append('category_id', filters.category_id);
            if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.hide_closed) params.append('hide_closed', 'true');
            params.append('page', pagination.page);
            params.append('limit', pagination.limit);
            params.append('sort_field', sortConfig.field);
            params.append('sort_direction', sortConfig.direction);

            const response = await api.get(`/helpdesk-agent/tickets?${params.toString()}`);
            if (response.data.success) {
                setTickets(response.data.tickets);
                setPagination(prev => ({ ...prev, ...response.data.pagination }));
            }
        } catch (error) {
            console.error('Failed to load tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusToggle = (status) => {
        setFilters(prev => {
            const newStatuses = prev.status.includes(status)
                ? prev.status.filter(s => s !== status)
                : [...prev.status, status];
            return { ...prev, status: newStatuses };
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSort = (field) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (field) => {
        if (sortConfig.field !== field) return '↕';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadTickets();
    };

 const clearFilters = () => {
    sessionStorage.removeItem('helpdesk_filters');
    sessionStorage.removeItem('helpdesk_sort');
    setFilters({
        search: '',
        status: ['open', 'assigned', 'in_progress', 'waiting_for_customer'],
        priority: '',
        category_id: '',
        assigned_to: '',
        date_from: '',
        date_to: '',
        hide_closed: false
    });
    setSortConfig({ field: 'created_at', direction: 'desc' });
    setPagination(prev => ({ ...prev, page: 1 }));
};


    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const locale = language === 'ar' ? 'ar-EG' : 'en-US';
        return new Date(dateString).toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'open': { label: t('statusOpen'), className: 'open' },
            'assigned': { label: t('statusAssigned'), className: 'assigned' },
            'in_progress': { label: t('statusInProgress'), className: 'in-progress' },
            'waiting_for_customer': { label: t('statusWaiting'), className: 'waiting' },
            'resolved': { label: t('statusResolved'), className: 'resolved' },
            'closed': { label: t('statusClosed'), className: 'closed' }
        };
        const s = statusMap[status] || { label: status, className: 'default' };
        return <span className={`hd-status-badge ${s.className}`}>{s.label}</span>;
    };

    const getPriorityBadge = (priority) => {
        const priorityMap = {
            'low': { label: t('priorityLow'), className: 'low' },
            'medium': { label: t('priorityMedium'), className: 'medium' },
            'high': { label: t('priorityHigh'), className: 'high' },
            'critical': { label: t('priorityCritical'), className: 'critical' }
        };
        const p = priorityMap[priority] || { label: priority, className: 'default' };
        return <span className={`hd-priority-badge ${p.className}`}>{p.label}</span>;
    };

    const statuses = [
        { value: 'open', label: t('statusOpen') },
        { value: 'assigned', label: t('statusAssigned') },
        { value: 'in_progress', label: t('statusInProgress') },
        { value: 'waiting_for_customer', label: t('statusWaiting') },
        { value: 'resolved', label: t('statusResolved') },
        { value: 'closed', label: t('statusClosed') }
    ];

    return (
        <Layout>
            <div className={`hd-tickets-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="hd-header">
                    <h1>🎫 {t('allTickets')}</h1>
                    <div className="hd-header-buttons">
                        <button onClick={toggleLanguage} className="hd-btn hd-btn-lang">
                            🌐 {language === 'ar' ? 'English' : 'العربية'}
                        </button>
                        <Link to="/helpdesk/tickets/new" className="hd-btn hd-btn-success">
                            + {t('createNewTicket')}
                        </Link>
                        <span className="hd-ticket-count">{pagination.total} {t('tickets')}</span>
                    </div>
                </div>

                {/* Filters */}
                <div className="hd-filters-compact">
                    {/* Row 1: Search and Quick Filters */}
                    <div className="hd-filter-row">
                        <form onSubmit={handleSearch} className="hd-search-form">
                            <input
                                type="text"
                                placeholder={t('searchTickets')}
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="hd-search-input"
                            />
                            <button type="submit" className="hd-btn hd-btn-primary">{t('search')}</button>
                        </form>

                        <select
                            value={filters.priority}
                            onChange={(e) => handleFilterChange('priority', e.target.value)}
                            className="hd-select-sm"
                        >
                            <option value="">{t('allPriorities')}</option>
                            <option value="low">{t('priorityLow')}</option>
                            <option value="medium">{t('priorityMedium')}</option>
                            <option value="high">{t('priorityHigh')}</option>
                            <option value="critical">{t('priorityCritical')}</option>
                        </select>

                        <select
                            value={filters.category_id}
                            onChange={(e) => handleFilterChange('category_id', e.target.value)}
                            className="hd-select-sm"
                        >
                            <option value="">{t('allCategories')}</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>

                        <select
                            value={filters.assigned_to}
                            onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
                            className="hd-select-sm"
                        >
                            <option value="">{t('allAssignments')}</option>
                            <option value="me">{t('myTickets')}</option>
                            <option value="unassigned">{t('unassigned')}</option>
                            {agents.map(agent => (
                                <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Row 2: Status Multi-select, Date Range, Hide Closed */}
                    <div className="hd-filter-row">
                        <div className="hd-status-chips">
                            <span className="hd-filter-label">{t('status')}:</span>
                            {statuses.map(s => (
                                <label key={s.value} className={`hd-status-chip ${filters.status.includes(s.value) ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={filters.status.includes(s.value)}
                                        onChange={() => handleStatusToggle(s.value)}
                                    />
                                    {s.label}
                                </label>
                            ))}
                        </div>

                        <div className="hd-date-range">
                            <span className="hd-filter-label">{t('date')}:</span>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                className="hd-date-input"
                            />
                            <span>{t('to')}</span>
                            <input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                className="hd-date-input"
                            />
                        </div>

                        <label className="hd-checkbox-filter">
                            <input
                                type="checkbox"
                                checked={filters.hide_closed}
                                onChange={(e) => handleFilterChange('hide_closed', e.target.checked)}
                            />
                            {t('hideClosed')}
                        </label>

                        <button onClick={clearFilters} className="hd-btn hd-btn-secondary hd-btn-sm">
                            {t('clearFilters')}
                        </button>
                    </div>
                </div>

                {/* Tickets Table */}
                <div className="hd-table-container">
                    {loading ? (
                        <div className="hd-loading">{t('loading')}</div>
                    ) : (
                        <table className="hd-table hd-table-sortable">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('ticket_number')} className="hd-sortable">
                                        {t('ticketNumber')} {getSortIcon('ticket_number')}
                                    </th>
                                    <th onClick={() => handleSort('subject')} className="hd-sortable">
                                        {t('subject')} {getSortIcon('subject')}
                                    </th>
                                    <th onClick={() => handleSort('client_name')} className="hd-sortable">
                                        {t('client')} {getSortIcon('client_name')}
                                    </th>
                                    <th>{t('company')}</th>
                                    <th onClick={() => handleSort('status')} className="hd-sortable">
                                        {t('status')} {getSortIcon('status')}
                                    </th>
                                    <th onClick={() => handleSort('priority')} className="hd-sortable">
                                        {t('priority')} {getSortIcon('priority')}
                                    </th>
                                    <th>{t('assignedTo')}</th>
                                    <th onClick={() => handleSort('created_at')} className="hd-sortable">
                                        {t('created')} {getSortIcon('created_at')}
                                    </th>
                                    <th onClick={() => handleSort('last_reply_at')} className="hd-sortable">
                                        {t('lastReply')} {getSortIcon('last_reply_at')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="hd-empty">{t('noTicketsFound')}</td>
                                    </tr>
                                ) : (
                                    tickets.map(ticket => (
                                        <tr key={ticket.id}>
                                            <td>
                                                <Link to={`/helpdesk/tickets/${ticket.id}`} className="hd-ticket-link">
                                                    #{ticket.ticket_number}
                                                </Link>
                                            </td>
                                            <td className="hd-subject">{ticket.subject}</td>
                                            <td>
                                                <div className="hd-client-info">
                                                    <span className="hd-client-name">{ticket.client_name || t('unknown')}</span>
                                                    <span className="hd-client-email">{ticket.client_email}</span>
                                                </div>
                                            </td>
                                            <td>{ticket.client_company || '-'}</td>
                                            <td>{getStatusBadge(ticket.status)}</td>
                                            <td>{getPriorityBadge(ticket.priority)}</td>
                                            <td className="hd-assigned">
                                                {ticket.assigned_agents || <span className="hd-unassigned">{t('unassigned')}</span>}
                                            </td>
                                            <td>{formatDate(ticket.created_at)}</td>
                                            <td>{formatDate(ticket.last_reply_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="hd-pagination">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="hd-btn hd-btn-sm"
                        >
                            {t('previous')}
                        </button>
                        <span className="hd-page-info">
                            {t('page')} {pagination.page} {t('of')} {pagination.pages}
                        </span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.pages}
                            className="hd-btn hd-btn-sm"
                        >
                            {t('next')}
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default HelpdeskTickets;
